/**
 * ALGERIAN COMPLIANCE ENGINE
 * 
 * Determines which tax and legal obligations apply to a specific business
 * based on their legal form, tax regime, activity, revenue, employees, etc.
 * 
 * Based on Algerian tax law 2024-2025
 */

import type { CompanyLegalProfile } from '@/types';
import { ALGERIAN_COMPLIANCE_OBLIGATIONS, type ComplianceObligation } from './obligations';

export interface ApplicableObligation extends ComplianceObligation {
  is_applicable: boolean;
  reason_not_applicable?: string;
  next_deadline?: Date;
  days_until_deadline?: number;
}

export interface ComplianceAnalysis {
  total_obligations: number;
  applicable_obligations: ApplicableObligation[];
  not_applicable_obligations: ApplicableObligation[];
  
  // Grouped by frequency
  monthly: ApplicableObligation[];
  quarterly: ApplicableObligation[];
  annual: ApplicableObligation[];
  one_time: ApplicableObligation[];
  
  // Grouped by category
  tax: ApplicableObligation[];
  social: ApplicableObligation[];
  legal: ApplicableObligation[];
  accounting: ApplicableObligation[];
  
  // Upcoming deadlines (next 90 days)
  upcoming_deadlines: ApplicableObligation[];
  
  // Estimated annual cost
  estimated_penalty_risk: number; // Total potential penalties if late
}

/**
 * Analyze which obligations apply to a company
 */
export function analyzeCompliance(profile: CompanyLegalProfile): ComplianceAnalysis {
  const obligations = ALGERIAN_COMPLIANCE_OBLIGATIONS.filter(o => o.is_active);
  
  const applicableObligations: ApplicableObligation[] = [];
  const notApplicableObligations: ApplicableObligation[] = [];
  
  for (const obligation of obligations) {
    const { is_applicable, reason } = checkApplicability(obligation, profile);
    
    const applicableObligation: ApplicableObligation = {
      ...obligation,
      is_applicable,
      reason_not_applicable: reason,
      next_deadline: calculateNextDeadline(obligation),
    };
    
    if (applicableObligation.next_deadline) {
      applicableObligation.days_until_deadline = Math.ceil(
        (applicableObligation.next_deadline.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
      );
    }
    
    if (is_applicable) {
      applicableObligations.push(applicableObligation);
    } else {
      notApplicableObligations.push(applicableObligation);
    }
  }
  
  // Group by frequency
  const monthly = applicableObligations.filter(o => o.frequency === 'monthly');
  const quarterly = applicableObligations.filter(o => o.frequency === 'quarterly');
  const annual = applicableObligations.filter(o => o.frequency === 'annual');
  const one_time = applicableObligations.filter(o => o.frequency === 'one_time');
  
  // Group by category
  const tax = applicableObligations.filter(o => o.category === 'tax');
  const social = applicableObligations.filter(o => o.category === 'social');
  const legal = applicableObligations.filter(o => o.category === 'legal');
  const accounting = applicableObligations.filter(o => o.category === 'accounting');
  
  // Upcoming deadlines (next 90 days)
  const upcoming_deadlines = applicableObligations
    .filter(o => o.days_until_deadline !== undefined && o.days_until_deadline >= 0 && o.days_until_deadline <= 90)
    .sort((a, b) => (a.days_until_deadline || 0) - (b.days_until_deadline || 0));
  
  // Estimated penalty risk
  const estimated_penalty_risk = applicableObligations
    .filter(o => o.has_penalty && o.penalty_amount)
    .reduce((sum, o) => sum + (o.penalty_amount || 0), 0);
  
  return {
    total_obligations: obligations.length,
    applicable_obligations: applicableObligations,
    not_applicable_obligations: notApplicableObligations,
    monthly,
    quarterly,
    annual,
    one_time,
    tax,
    social,
    legal,
    accounting,
    upcoming_deadlines,
    estimated_penalty_risk,
  };
}

/**
 * Check if an obligation applies to a company
 */
function checkApplicability(
  obligation: ComplianceObligation,
  profile: CompanyLegalProfile
): { is_applicable: boolean; reason?: string } {
  
  // 1. Check if applies to all
  if (obligation.applies_to_all) {
    return { is_applicable: true };
  }
  
  // 2. Check legal form
  if (obligation.applies_to_legal_forms && obligation.applies_to_legal_forms.length > 0) {
    if (!obligation.applies_to_legal_forms.includes(profile.legal_form || '')) {
      return {
        is_applicable: false,
        reason: `Non applicable à ${profile.legal_form}. Formes concernées: ${obligation.applies_to_legal_forms.join(', ')}`,
      };
    }
  }
  
  // 3. Check tax regime
  if (obligation.applies_to_tax_regimes && obligation.applies_to_tax_regimes.length > 0) {
    if (!obligation.applies_to_tax_regimes.includes(profile.tax_regime || '')) {
      return {
        is_applicable: false,
        reason: `Non applicable au régime ${profile.tax_regime}. Régimes concernés: ${obligation.applies_to_tax_regimes.join(', ')}`,
      };
    }
  }
  
  // 4. Check activity type
  if (obligation.applies_to_activities && obligation.applies_to_activities.length > 0) {
    if (!obligation.applies_to_activities.includes(profile.activity_type || '')) {
      return {
        is_applicable: false,
        reason: `Non applicable aux activités de ${profile.activity_type}`,
      };
    }
  }
  
  // 5. Check minimum employees
  if (obligation.min_employees !== undefined) {
    if ((profile.employee_count || 0) < obligation.min_employees) {
      return {
        is_applicable: false,
        reason: `Minimum ${obligation.min_employees} employé(s) requis`,
      };
    }
  }
  
  // 6. Check minimum revenue
  if (obligation.min_revenue !== undefined) {
    if ((profile.estimated_annual_revenue || 0) < obligation.min_revenue) {
      return {
        is_applicable: false,
        reason: `CA minimum: ${(obligation.min_revenue / 1000000).toFixed(0)}M DZD`,
      };
    }
  }
  
  // 7. Check maximum revenue (for IFU threshold)
  if (obligation.max_revenue !== undefined) {
    if ((profile.estimated_annual_revenue || 0) > obligation.max_revenue) {
      return {
        is_applicable: false,
        reason: `CA maximum dépassé: ${(obligation.max_revenue / 1000000).toFixed(0)}M DZD`,
      };
    }
  }
  
  // 8. Check export requirement
  if (obligation.requires_export && !profile.is_exporter) {
    return {
      is_applicable: false,
      reason: 'Réservé aux exportateurs',
    };
  }
  
  // 9. Check import requirement
  if (obligation.requires_import && !profile.is_importer) {
    return {
      is_applicable: false,
      reason: 'Réservé aux importateurs',
    };
  }
  
  // All checks passed
  return { is_applicable: true };
}

/**
 * Calculate next deadline for an obligation
 */
function calculateNextDeadline(obligation: ComplianceObligation): Date | undefined {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth(); // 0-11
  const currentDay = today.getDate();
  
  if (obligation.frequency === 'monthly') {
    // Next occurrence this month or next month
    const thisMonthDeadline = new Date(currentYear, currentMonth, obligation.deadline_day);
    
    if (currentDay < obligation.deadline_day) {
      return thisMonthDeadline;
    } else {
      // Next month
      return new Date(currentYear, currentMonth + 1, obligation.deadline_day);
    }
  }
  
  if (obligation.frequency === 'quarterly') {
    // Q1: March, Q2: June, Q3: September, Q4: December
    const quarterMonths = [2, 5, 8, 11]; // March, June, Sep, Dec (0-indexed)
    
    for (const month of quarterMonths) {
      const deadline = new Date(currentYear, month, obligation.deadline_day);
      if (deadline > today) {
        return deadline;
      }
    }
    
    // Next year Q1
    return new Date(currentYear + 1, 2, obligation.deadline_day);
  }
  
  if (obligation.frequency === 'annual') {
    const month = (obligation.deadline_month || 1) - 1; // Convert to 0-indexed
    const thisYearDeadline = new Date(currentYear, month, obligation.deadline_day);
    
    if (thisYearDeadline > today) {
      return thisYearDeadline;
    } else {
      return new Date(currentYear + 1, month, obligation.deadline_day);
    }
  }
  
  if (obligation.frequency === 'biannual') {
    // June 30 and December 31
    const jun30 = new Date(currentYear, 5, 30);
    const dec31 = new Date(currentYear, 11, 31);
    
    if (today < jun30) return jun30;
    if (today < dec31) return dec31;
    return new Date(currentYear + 1, 5, 30);
  }
  
  // one_time obligations don't have recurring deadlines
  return undefined;
}

/**
 * Get obligations due in next N days
 */
export function getUpcomingObligations(
  profile: CompanyLegalProfile,
  daysAhead: number = 30
): ApplicableObligation[] {
  const analysis = analyzeCompliance(profile);
  
  return analysis.applicable_obligations
    .filter(o => {
      if (!o.next_deadline) return false;
      const daysUntil = o.days_until_deadline || 0;
      return daysUntil >= 0 && daysUntil <= daysAhead;
    })
    .sort((a, b) => (a.days_until_deadline || 0) - (b.days_until_deadline || 0));
}

/**
 * Get overdue obligations (for notification system)
 */
export function getOverdueObligations(
  profile: CompanyLegalProfile,
  lastCompletionDates: Record<string, Date>
): ApplicableObligation[] {
  const analysis = analyzeCompliance(profile);
  const today = new Date();
  
  return analysis.applicable_obligations.filter(o => {
    if (!o.next_deadline) return false;
    
    const lastCompleted = lastCompletionDates[o.id];
    
    // If never completed and deadline passed
    if (!lastCompleted && o.next_deadline < today) {
      return true;
    }
    
    // If last completion was before the current deadline
    if (lastCompleted && o.next_deadline > lastCompleted && o.next_deadline < today) {
      return true;
    }
    
    return false;
  });
}

/**
 * Calculate total estimated annual compliance cost
 */
export function estimateAnnualComplianceCost(profile: CompanyLegalProfile): {
  monthly_obligations: number;
  quarterly_obligations: number;
  annual_obligations: number;
  total_annual: number;
} {
  const analysis = analyzeCompliance(profile);
  
  return {
    monthly_obligations: analysis.monthly.length * 12,
    quarterly_obligations: analysis.quarterly.length * 4,
    annual_obligations: analysis.annual.length,
    total_annual: (analysis.monthly.length * 12) + (analysis.quarterly.length * 4) + analysis.annual.length,
  };
}
